<template>
  <div class="event-documentation" redesign>
    <!-- Request stepper -->
    <RequestStepProgressBar step4 />

    <div class="form-container event-documentation-form">
      <!-- Information -->
      <div class="form-header">
        <span v-translate
          >In order to be eligible for a grant, you must document at least one emergency.</span
        >
      </div>

      <!-- Sample documentation -->
      <div class="document-information-container">
        <h4 class="form-field-header" v-translate>Your documents need to show:</h4>
        <div class="event-documentation-row-wrapper">
          <div class="form-field-container sample-document-container">
            <img :src="require('@/assets/images/Sample Document - Document your emergency.svg')" />
          </div>
          <div v-if="organizationDetails.documentationCopy" v-html="documentationCopy" class="form-field-container"></div>
        </div>
      </div>

      <!-- Information card -->
      <div class="info-container">
        <div class="tooltip-icon">
          <img src="@/assets/icons/information-icon.png" />
        </div>
        <div class="tooltip">
          <span class="tooltip-text" v-translate>
            If a document that describes the emergency is not in your name, you must document your
            connection to the person whose name is on the document. If it is in the name of someone
            in your household, please submit a document to show how you are related (i.e. a tax
            return, marriage license, or a bill with both your names and shared address on it).
          </span>
        </div>
      </div>

      <!-- Document uploads for each event type -->
      <div v-for="(event, i) in events" :key="i" class="event-uploads">
        <DocumentUpload
          :event="event"
          :documentList.sync="eventDocs[event.id]"
          :loading="loading"
          :docDetailType="event.docType"
          @documents-changed="onDocumentsChanged($event, event.id)"
          @delete-document="onDeleteDocument($event, event.id)"
          @document-detail-type-selected="updateDocumentsDetailType($event, event.id)"
          :actionName="`upload-event-${event.categoryRef.humanReadableId}-document`"
        />
      </div>

      <!-- Additional information -->
      <div class="form-field request-questions">
        <h4 class="form-field-header" v-translate>
          Please add any additional information that will help us understand your document(s):
        </h4>
        <div id="md-textarea">
          <textarea
            class="form-field-textarea"
            @input="mixin_TextAreaResize_resize"
            rows="1"
            v-model="currentRequest.eventDocumentationMisc"
            @blur="saveFieldUpdate"
            data-action="event-additional-info"
          />
        </div>
      </div>

      <!-- No emergency error -->
      <div v-if="displayDocumentError" class="form-field error-container">
        <div class="tooltip-icon">
          <img src="@/assets/icons/error-icon.png" />
        </div>
        <div class="tooltip">
          <h4 class="tooltip-header" v-translate>No document uploaded</h4>
          <span class="tooltip-text" v-translate>
            In order to be eligible for a grant, you must document at least one event.
          </span>
        </div>
      </div>

      <!-- Next step -->
      <div class="form-footer">
        <div class="next-step">
          <translate>Next step:</translate><br /><translate class="next-step-name"
            >Describe expenses and amounts needed</translate
          >
        </div>
      </div>

      <!-- Submit -->
      <SubmitButton
        :loading="loading"
        :onContinue="confirmDocs"
        :noNavigation="!allowNavigationWithoutDocumentation()"
        actionName="commit-event-documents"
      />
    </div>

    <!-- Documentation confirmation modal -->
    <Modal
      :additionalModalStyles="{
        width: '100%',
        'max-width': '500px',
        color: 'white',
        border: 'none',
      }"
      v-if="docConfirmationModal"
      dimmed
    >
      <div class="form-container card-shape modal-window">
        <div class="form-field">
          <h4 class="form-field-header" v-translate>Please confirm that your document(s):</h4>
          <div class="form-field-container">
            <ul>
              <li v-translate>Describe the emergency</li>
              <li v-translate>Show your name (or that of someone in your household)</li>
              <li v-translate>Show the document date</li>
              <li v-translate>
                Show the name and information of the entity that issued the document
              </li>
              <li v-translate>Are readable, not blurry</li>
              <li v-translate>
                Are complete; include the entire page, including headers and footers
              </li>
              <li v-translate>blackOutPersonalInformation</li>
            </ul>
          </div>
        </div>
        <div class="button-container frow" style="justify-content: space-between">
          <button class="form-button white-button" @click="docConfirmationModal = false">
            <span v-translate>Go back</span>
          </button>

          <SubmitButton
            :loading="loading"
            :onContinue="
              () => {
                docConfirmationModal = false;
                onNext();
              }
            "
            actionName="confirm-event-documents"
          />
        </div>
      </div>
      <!-- pop up here, display after click -->
    </Modal>

    <!-- Skip event documentation modal -->
    <Modal
      :additionalModalStyles="{
        width: '100%',
        'max-width': '500px',
        border: 'none',
      }"
      v-if="skipEventDocumentationModal"
      dimmed
    >
      <div class="form-container card-shape modal-window">
        <div class="form-field">
          <h4 class="form-field-header" v-translate>No Documentation Required</h4>
          <div class="form-field-container">
            <p>Good news! The event(s) you have selected do not require you to upload documenation for this step.</p>
            <p>Please click 'Next' to continue.</p>
          </div>
        </div>
        <div class="button-container frow">
          <SubmitButton
            :loading="loading"
            :onContinue="() => {
              skipEventDocumentationModal = false;
              onNext();
            }"
          />
        </div>
      </div>
    </Modal>
  </div>
</template>

<script>
import DocumentUpload from '@/components/requests/DocumentUpload'
import Document from '@/services/documents'
import { mapState } from 'vuex'
import Request from '@/services/requests'
import LoadSpinner from '@/components/LoadSpinner'
import TextAreaResize from '../../mixins/TextAreaResize'
import { analyticsMixin } from '@/services/googleAnalytics'
import EventCategory from '@/services/eventCategories'
import Modal from '@/components/Modal'
import RequestStepProgressBar from '@/components/stepper/RequestStepProgressBar'
import SubmitButton from '@/components/requests/SubmitButton'
import UserEvent from '@/services/userEvents'
import { formatText, formatValue } from '@/services/utils'

export default {
  name: 'EventDocumentation',
  mixins: [analyticsMixin, TextAreaResize],
  components: {
    DocumentUpload,
    LoadSpinner,
    Modal,
    RequestStepProgressBar,
    SubmitButton,
  },
  data() {
    return {
      events: [],
      eventDocs: null,
      eventsWithAdditional: [],
      killList: [],
      loading: true,
      placeholder: this.$gettext('Answer'),
      docConfirmationModal: false,
      displayDocumentError: false,
      skipEventDocumentationModal: false,
    }
  },
  created() {
    this.setDocs()
    this.skipEventDocumentationModal = !this.hasEventCategoryWithEventDocumentation
    // this.startEditingPing()
    this.loading = false
  },
  mounted() {
    // Update the current request completion status
    UserEvent.api.requestUpdateCompletionStatus(
      this.currentRequest.id,
      UserEvent.options.REQUEST_STEP_EVENT_DOCUMENTATION,
    )
  },
  destroyed() {
    clearInterval(this.interval)
  },
  methods: {
    async confirmDocs() {
      if (this.noDocumentsUploaded() && this.organizationDetails.eventDocumentationEnabled) {
        this.displayDocumentError = true
        return
      }

      if (this.noDocumentsUploaded() && !this.organizationDetails.eventDocumentationEnabled) {
        return
      }

      this.docConfirmationModal = true
    },
    noDocumentsUploaded() {
      return Object.values(this.eventDocs).every((eventDoc) => eventDoc.length == 0)
    },
    allowNavigationWithoutDocumentation() {
      return this.noDocumentsUploaded && !this.organizationDetails.eventDocumentationEnabled
    },

    setDocs() {
      // Create an object of all event documents:
      //  - Each key is the ID of an event
      //  - Each value is an array of documents for that events
      const eventDocs = {}
      this.currentRequest.eventsRef.forEach(event => {
        eventDocs[event.id] = [...event.documentsRef]
      })
      this.eventDocs = eventDocs
      this.events = this.currentRequest.eventsRef.slice()
    },
    async onDocumentsChanged(documentList, eventId) {
      this.loading = true
      this.eventsTracker('uploaded-or-changed-upload-for-event', `Event ID: ${eventId}`)
      // Replace all documents for this particular event with the newest set
      this.eventDocs[eventId] = documentList
      await this.updateDocuments()
      this.loading = false
    },
    async onDeleteDocument(docToDelete, eventId) {
      this.loading = true
      // If a document the user wants to delete has an ID, it means it exists in the database.
      // That means we need to keep track of it, and send a command to delete it later.
      if (docToDelete.id) {
        this.killList.push(docToDelete)
      }
      this.eventDocs[eventId] = this.eventDocs[eventId].filter(doc => doc.id !== docToDelete.id)

      await this.updateDocuments()
      this.loading = false
    },
    async updateDocuments() {
      // Collect event-docs into an array of arrays
      const documentList = Object.values(this.eventDocs)

      // Loop through all documents from each event
      const promiseList = []
      documentList.forEach(eventDocs => {
        eventDocs.forEach(doc => {
          const index = eventDocs.indexOf(doc)
          // If document does not have an ID, it doesn't yet exist in the DB - so we need to create it
          if (!doc.id) {
            promiseList.push(
              Document.api.create(doc).then(document => (eventDocs[index] = document)),
            )
          }
        })
      })

      // Delete every document the user removed
      this.killList.forEach(doc => {
        promiseList.push(Document.api.delete(doc))
      })

      // Wait for all Documents to be modified before updating Request
      await Promise.all(promiseList)
      this.killList = []

      // Modify the request in state
      const request = this.currentRequest
      request.eventsRef.forEach(event => {
        event.documentsRef = this.eventDocs[event.id]
      })
      this.$store.dispatch('updateCurrentRequest', request)
      this.setDocs()
    },
    async onNext() {
      this.loading = true

      this.trackClickedCTA()
      // Collect event-docs into an array of arrays
      const documentList = Object.values(this.eventDocs)

      // Loop through all documents from each event
      const promiseList = []
      documentList.forEach(eventDocs => {
        eventDocs.forEach(doc => {
          // If document does not have an ID, it doesn't yet exist in the DB - so we need to create it
          if (!doc.id) {
            promiseList.push(Document.api.create(doc))
          }
        })
      })

      // Delete every document the user removed
      this.killList.forEach(doc => {
        promiseList.push(Document.api.delete(doc))
      })

      // Wait for all Documents to be modified before updating Request
      await Promise.all(promiseList)

      // Update Request and push next screen
      const updateFields = ['eventDocumentationMisc']
      const response = await Request.api.update(this.currentRequest, updateFields)
      await this.$store.dispatch('updateCurrentRequest', response)

      this.loading = false
      return response
    },
    async saveFieldUpdate() {
      const updateFields = ['eventDocumentationMisc']
      const response = await Request.api.update(this.currentRequest, updateFields)
      await this.$store.dispatch('updateCurrentRequest', response)
    },
    startEditingPing() {
      const request = this.currentRequest
      Request.api.editingPing(request)
      this.interval = setInterval(function() {
        Request.api.editingPing(request)
      }, 10000)
    },
    async updateDocumentsDetailType(docDetailType, eventId) {
      await this.$store.dispatch('partialUpdateEvent', { id: eventId, docType: docDetailType })
    },
  },
  computed: {
    ...mapState(['currentRequest', 'organizationDetails']),
    disabled() {
      return (
        this.loading ||
        (!this.areAdditionalQuestionsAnswered &&
          this.organizationDetails.eventDocumentationEnabled &&
          this.organizationDetails.expenseDocumentationEnabled)
      )
    },
    areAdditionalQuestionsAnswered() {
      const eventsWithAdditional = this.currentRequest.eventsRef.filter(event => {
        return (
          [
            EventCategory.options.HOME,
            EventCategory.options.CAR,
            EventCategory.options.MEDICAL,
          ].includes(event.categoryRef.nameEn) && event.documentsRef.length > 0
        )
      })
      return eventsWithAdditional.every(event => {
        return event.docType != null && event.docType !== undefined && event.docType != 'remove'
      })
    },
    documentationCopy() {
      const varMap = { org: this.organizationDetails }
      return formatValue(this.organizationDetails.documentationCopy, varMap)
    },
    hasEventCategoryWithEventDocumentation() {
      return this.events.some(event => EventCategory.options.EVENT_DOCUMENTATION_REQUIRED.includes(event.categoryRef.requiredDocumentation) || !event.categoryRef.requiredDocumentation)
    },
  },
}
</script>

<style lang="scss" scoped>
@import '@/styles/variables';
@import '@/styles/mixins/buttons';
@import '@/styles/mixins/containers';
@import '@/styles/flex-box';
@import '@/styles/grant-circles';

.event-documentation {
  display: flex;
  flex-direction: column;

  .event-documentation-form {
    @extend .constrain-tablet-and-up;

    @media (min-width: 1140px) {
      max-width: 1076px !important;
    }
  }

  @include tablet-and-up-screen {
    .event-documentation-row-wrapper {
      display: flex;
      flex-direction: row;

      .sample-document-container {
        min-width: 320px;
        margin: 0;
      }
    }
  }
}

::v-deep .document-information-container {
  @extend .card-shape;
  background-color: rgba(0, 42, 139, 0.1);
  box-shadow: none;
  -webkit-box-shadow: none;
  margin-bottom: 2.5rem;

  .form-field-header {
    font-size: 1.5rem;
    line-height: 2rem;
  }

  .sample-document-container {
    text-align: center;
    margin-top: 1.5rem;
    margin-bottom: 1rem;

    img {
      width: 80%;
    }

    @include mobile-screen {
      img {
        max-width: 320px;
      }
    }

    @include tablet-and-up-screen {
      img {
        min-width: 266px;
      }
    }

    @include tablet-screen {
      text-align: left;
    }

    @include desktop-screen {
      margin-right: 2rem !important;
    }
  }

  .document-requirements-list {
    padding: 0;
    list-style: none;

    li {
      margin-left: 0;
      margin-bottom: 1rem;

      &:before {
        background-color: #212b36;
        border-radius: 50%;
        color: $white;
        content: var(--n);
        display: inline-block;
        height: 22px;
        text-align: center;
        width: 23px;
        vertical-align: top;
      }

      span {
        display: inline-block;
        padding-left: 0.75rem;
        width: 90%;
        font-size: 1.125rem;
        line-height: 1.5rem;
        font-weight: bold;
      }
    }
  }
}
</style>
